<template>
  <transition name="filter-checkpoints-modal" appear>
    <div
      v-if="value"
      class="filter-checkpoints-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-checkpoints-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-checkpoints-modal__card">
        <div class="filter-checkpoints-modal__header">
          <span id="filter-checkpoints-modal-title" class="filter-checkpoints-modal__title">
            Filter checkpoints
          </span>
          <button
            type="button"
            class="filter-checkpoints-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-checkpoints-modal__filters-row">
          <div class="filter-checkpoints-modal__column">
            <span class="filter-checkpoints-modal__column-title">Type</span>
            <div class="filter-checkpoints-modal__options">
              <CheckboxInput
                v-for="option in typeOptions"
                :key="option.value"
                :label="option.label"
                :value="types[option.value]"
                @input="updateType(option.value, $event)"
              />
            </div>
          </div>

          <div class="filter-checkpoints-modal__column">
            <span class="filter-checkpoints-modal__column-title">Status</span>
            <div class="filter-checkpoints-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div class="filter-checkpoints-modal__column filter-checkpoints-modal__column--radio">
            <RadioInput
              label="Archived"
              :availableItems="archivedOptions"
              :checkedItem="includeArchived ? ARCHIVED_SHOW_LABEL : ARCHIVED_HIDE_LABEL"
              :uncheckedValue="archivedClearKey"
              @changeradio="onArchivedChange"
            />
          </div>
        </div>

        <div class="filter-checkpoints-modal__divider" />

        <div class="filter-checkpoints-modal__content">
          <RadioInput
            label="Content"
            :availableItems="contentOptions"
            :checkedItem="contentCheckedLabel"
            :uncheckedValue="contentClearKey"
            @changeradio="onContentChange"
          />
        </div>

        <div class="filter-checkpoints-modal__divider" />

        <div class="filter-checkpoints-modal__footer">
          <button type="button" class="filter-checkpoints-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";

/** Sentinel key for Inspector Only; emitted as `null` in the type array. */
const INSPECTOR_ONLY_KEY = "inspector_only";

/** UI type keys → stable lowercase snake_case values (inspector_only is emitted as `null`). */
const TYPE_VALUES = {
  VIDEO: "video",
  TASK_AN_EMPLOYEE: "task_an_employee",
  ARTICLE: "article",
  INSPECTOR_ONLY: INSPECTOR_ONLY_KEY,
};

const STATUS_VALUES = {
  DRAFT: "draft",
  ACTIVE: "active",
  RETIRED: "retired",
};

const CONTENT_VALUES = {
  BOTH_REQUIRED: "both_required",
  SKIP_INSPECTION: "skip_inspection",
  SKIP_QUIZ: "skip_quiz",
};

const ARCHIVED_HIDE_LABEL = "Do Not Show";
const ARCHIVED_SHOW_LABEL = "Show";
const CONTENT_NO_FILTER_LABEL = "No Filter";

const CONTENT_LABEL_BY_VALUE = {
  [CONTENT_VALUES.BOTH_REQUIRED]: "Quiz and Protocol",
  [CONTENT_VALUES.SKIP_INSPECTION]: "Quiz only",
  [CONTENT_VALUES.SKIP_QUIZ]: "Inspection Protocol only",
};

const CONTENT_VALUE_BY_LABEL = {
  [CONTENT_NO_FILTER_LABEL]: null,
  "Quiz and Protocol": CONTENT_VALUES.BOTH_REQUIRED,
  "Quiz only": CONTENT_VALUES.SKIP_INSPECTION,
  "Inspection Protocol only": CONTENT_VALUES.SKIP_QUIZ,
};

const DEFAULT_TYPES = {
  [TYPE_VALUES.VIDEO]: true,
  [TYPE_VALUES.TASK_AN_EMPLOYEE]: true,
  [TYPE_VALUES.ARTICLE]: true,
  [TYPE_VALUES.INSPECTOR_ONLY]: true,
};

const DEFAULT_STATUSES = {
  [STATUS_VALUES.DRAFT]: true,
  [STATUS_VALUES.ACTIVE]: true,
  [STATUS_VALUES.RETIRED]: false,
};

const DEFAULT_INCLUDE_ARCHIVED = false;
const DEFAULT_CONTENT = null;

const props = defineProps({
  value: { type: Boolean, default: false },
  initialTypes: {
    type: Object,
    default: () => ({
      video: true,
      task_an_employee: true,
      article: true,
      inspector_only: true,
    }),
  },
  initialStatuses: {
    type: Object,
    default: () => ({
      draft: true,
      active: true,
      retired: false,
    }),
  },
  initialIncludeArchived: { type: Boolean, default: false },
  initialContent: {
    type: String,
    default: null,
    validator: (value) =>
      value === null || ["both_required", "skip_inspection", "skip_quiz"].includes(value),
  },
});

const emit = defineEmits(["input", "close", "apply", "reset"]);

const typeOptions = [
  { label: "Video", value: TYPE_VALUES.VIDEO },
  { label: "Task an employee", value: TYPE_VALUES.TASK_AN_EMPLOYEE },
  { label: "Article", value: TYPE_VALUES.ARTICLE },
  { label: "Inspector Only", value: TYPE_VALUES.INSPECTOR_ONLY },
];

const statusOptions = [
  { label: "Draft", value: STATUS_VALUES.DRAFT },
  { label: "Active", value: STATUS_VALUES.ACTIVE },
  { label: "Retired", value: STATUS_VALUES.RETIRED },
];

const archivedOptions = [{ label: ARCHIVED_HIDE_LABEL }, { label: ARCHIVED_SHOW_LABEL }];
const contentOptions = [
  { label: CONTENT_NO_FILTER_LABEL },
  { label: "Quiz and Protocol" },
  { label: "Quiz only" },
  { label: "Inspection Protocol only" },
];

const archivedClearKey = ref("");
const contentClearKey = ref("");

const types = ref({ ...DEFAULT_TYPES, ...(props.initialTypes || {}) });
const statuses = ref({ ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) });
const includeArchived = ref(props.initialIncludeArchived);
const content = ref(props.initialContent);

const contentCheckedLabel = computed(() => {
  if (content.value == null) return CONTENT_NO_FILTER_LABEL;
  return CONTENT_LABEL_BY_VALUE[content.value] || CONTENT_NO_FILTER_LABEL;
});

watch(
  () => props.value,
  (open) => {
    if (open) syncFromProps();
  },
  { immediate: true }
);

function syncFromProps() {
  types.value = { ...DEFAULT_TYPES, ...(props.initialTypes || {}) };
  statuses.value = { ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) };
  includeArchived.value = props.initialIncludeArchived;
  content.value = props.initialContent;
}

function updateType(key, checked) {
  types.value = { ...types.value, [key]: !!checked };
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  includeArchived.value = payload?.value === ARCHIVED_SHOW_LABEL;
}

function onContentChange(payload) {
  const label = payload?.value;
  if (label == null || label === CONTENT_NO_FILTER_LABEL) {
    content.value = null;
    return;
  }
  content.value = Object.prototype.hasOwnProperty.call(CONTENT_VALUE_BY_LABEL, label)
    ? CONTENT_VALUE_BY_LABEL[label]
    : null;
}

function selectedTypeValues() {
  return typeOptions
    .filter((option) => types.value[option.value])
    .map((option) => (option.value === INSPECTOR_ONLY_KEY ? null : option.value));
}

function selectedStatusValues() {
  return statusOptions
    .filter((option) => statuses.value[option.value])
    .map((option) => option.value);
}

function handleReset() {
  types.value = { ...DEFAULT_TYPES };
  statuses.value = { ...DEFAULT_STATUSES };
  includeArchived.value = DEFAULT_INCLUDE_ARCHIVED;
  content.value = DEFAULT_CONTENT;
  archivedClearKey.value = `reset-${Date.now()}`;
  contentClearKey.value = `reset-content-${Date.now()}`;
  emit("reset");
}

function handleApply() {
  emit("apply", {
    type: selectedTypeValues(),
    status: selectedStatusValues(),
    includeArchived: includeArchived.value,
    content: content.value,
  });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-checkpoints-modal-enter-active,
.filter-checkpoints-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-checkpoints-modal-enter-from,
.filter-checkpoints-modal-leave-to {
  opacity: 0;
}

.filter-checkpoints-modal-enter-active .filter-checkpoints-modal__card,
.filter-checkpoints-modal-leave-active .filter-checkpoints-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.filter-checkpoints-modal-enter-from .filter-checkpoints-modal__card,
.filter-checkpoints-modal-leave-to .filter-checkpoints-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-checkpoints-modal-enter-active,
  .filter-checkpoints-modal-leave-active,
  .filter-checkpoints-modal-enter-active .filter-checkpoints-modal__card,
  .filter-checkpoints-modal-leave-active .filter-checkpoints-modal__card {
    transition: none !important;
  }
}

.filter-checkpoints-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-checkpoints-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-checkpoints-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-checkpoints-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-checkpoints-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-checkpoints-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-checkpoints-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-checkpoints-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-checkpoints-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-checkpoints-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-checkpoints-modal__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 8px;
}

.filter-checkpoints-modal__options ::v-deep .filter-checkbox {
  min-height: 20px;
  height: 20px;
}

.filter-checkpoints-modal__options ::v-deep .icon-check .v-btn {
  width: 20px !important;
  height: 20px !important;
}

.filter-checkpoints-modal__column--radio ::v-deep > div,
.filter-checkpoints-modal__content ::v-deep > div {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-checkpoints-modal__column--radio ::v-deep .filter-items,
.filter-checkpoints-modal__content ::v-deep .filter-items {
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
  background: transparent !important;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--radio-group,
.filter-checkpoints-modal__content ::v-deep .v-input--radio-group {
  margin: 0;
  padding: 0;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--selection-controls,
.filter-checkpoints-modal__content ::v-deep .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--selection-controls .v-input__slot,
.filter-checkpoints-modal__content ::v-deep .v-input--selection-controls .v-input__slot {
  margin: 0;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--radio-group__input,
.filter-checkpoints-modal__content ::v-deep .v-input--radio-group__input {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-radio,
.filter-checkpoints-modal__content ::v-deep .v-radio {
  height: 20px !important;
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  align-items: center;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--selection-controls__input,
.filter-checkpoints-modal__content ::v-deep .v-input--selection-controls__input {
  width: 16px !important;
  height: 16px !important;
  margin: 0 8px 0 0 !important;
  align-self: center;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--selection-controls__ripple,
.filter-checkpoints-modal__content ::v-deep .v-input--selection-controls__ripple {
  display: none;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-icon,
.filter-checkpoints-modal__content ::v-deep .v-icon {
  font-size: 16px !important;
  height: 16px !important;
  width: 16px !important;
  line-height: 16px !important;
}

.filter-checkpoints-modal__column--radio ::v-deep .v-input--selection-controls .v-radio > .v-label,
.filter-checkpoints-modal__content ::v-deep .v-input--selection-controls .v-radio > .v-label {
  margin-left: 0 !important;
  line-height: 20px;
  height: 20px;
}

.filter-checkpoints-modal__column--radio ::v-deep .filter-radio .mdi-radiobox-marked::before,
.filter-checkpoints-modal__content ::v-deep .filter-radio .mdi-radiobox-marked::before {
  content: url("../assets/checkedRadioIconRing.svg") !important;
}

.filter-checkpoints-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-checkpoints-modal__content {
  width: 100%;
}

.filter-checkpoints-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-checkpoints-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-checkpoints-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-checkpoints-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
